import React, { useState, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import GetCookie from "../../../utils/GetCookie";
import authNavigate from "../../../globalComponents/AuthNav";
import ProcessingPopup from "../../../utils/popup/ProcessingPopup";
import ErrorPopup from "../../../utils/popup/ErrorPopup";
import * as XLSX from "xlsx";

const API_BASE_URL = "/pceapi/slr/genai-rights";

const DOI_ALIASES   = ["doi", "digital object identifier"];
const PMID_ALIASES  = ["pmid", "pubmed id", "pubmed_id", "medline id", "medline_id", "pubmedid"];
const TITLE_ALIASES = ["title", "article title", "paper title", "study title"];

const CreateGenAIRightsCheckModal = ({ onClose, onSuccess }) => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [name, setName]             = useState("");
    const [file, setFile]             = useState(null);
    const [fileError, setFileError]   = useState("");
    const [isCreating, setIsCreating] = useState(false);

    const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
    const [processingMessage, setProcessingMessage] = useState("");
    const [popupErrorMessage, setPopupErrorMessage] = useState("");

    const headers = useMemo(() => ({
        Authorization: GetCookie("access_token"),
    }), []);

    /* Read just enough of the file to confirm it carries an identifier column,
       so the user finds out here rather than after the upload round-trip. */
    const validateFile = async (f) => {
        const isCsv = f.name.toLowerCase().endsWith(".csv");
        try {
            let rows;
            if (isCsv) {
                const text  = await f.text();
                const lines = text.split(/\r?\n/).slice(0, 32);
                rows = lines.map((l) => l.split(",").map((c) => c.replace(/^"|"$/g, "")));
            } else {
                const wb = XLSX.read(await f.arrayBuffer(), { type: "array", sheetRows: 32 });
                rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, defval: "" });
            }

            let headerRow = null;
            for (const row of rows) {
                const lower = row.map((v) => String(v || "").toLowerCase().trim());
                if (lower.some((v) => DOI_ALIASES.includes(v) || PMID_ALIASES.includes(v) || TITLE_ALIASES.includes(v))) {
                    headerRow = lower;
                    break;
                }
            }

            const hasDoi  = headerRow?.some((v) => DOI_ALIASES.includes(v));
            const hasPmid = headerRow?.some((v) => PMID_ALIASES.includes(v));

            if (!headerRow || (!hasDoi && !hasPmid)) {
                return "No DOI or PMID column found — add at least one identifier column and try again.";
            }
            return "";
        } catch {
            return "Could not read the file. Please upload a valid .xlsx, .xls or .csv file.";
        }
    };

    const handleFilePick = async (files) => {
        if (!files?.length) return;
        const f = files[0];
        setFile(f);
        setFileError("");
        const err = await validateFile(f);
        if (err) {
            setFileError(err);
            setFile(null);
        } else if (!name.trim()) {
            setName(f.name.replace(/\.[^/.]+$/, ""));
        }
    };

    const handleCreate = async () => {
        if (!file || fileError || isCreating) return;

        setIsCreating(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Uploading file...");
        setPopupErrorMessage("");

        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("name", name.trim() || file.name);

            const res = await axios.post(`${API_BASE_URL}/upload`, formData, {
                headers: { ...headers, "Content-Type": "multipart/form-data" },
            });

            if (res.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }

            if (res.data?.status === "success") {
                onSuccess && onSuccess(res.data.data);
            } else {
                setPopupErrorMessage(res.data?.message || "Failed to start the rights check");
            }
        } catch (error) {
            if (error.response?.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }
            setPopupErrorMessage(
                error.response?.data?.message || error?.message || "Error starting the rights check"
            );
        } finally {
            setIsCreating(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    return (
        <>
            <div className="ev-card-4 position-absolute z-1 create-search-dropdown p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="text-start">
                        <p className="ev-med-p-dark ev-bold-reg mb-0">New Rights Check</p>
                        <p className="ev-xsmall-p-dark mb-0 opacity-75">
                            Upload an Excel or CSV file with a DOI or PMID column
                        </p>
                    </div>

                    <button
                        type="button"
                        className="ev-card-3 ev-padding-xsmall2 border-0"
                        onClick={onClose}
                        disabled={isCreating}
                    >
                        <i className="bi bi-x ev-med-p-dark ev-fs-18"></i>
                    </button>
                </div>

                <div className="mb-3 text-start">
                    <label className="ev-small-p-dark ev-bold-reg mb-1">
                        Check Name
                    </label>
                    <input
                        type="text"
                        className="formField6 w-100"
                        placeholder="e.g. Pancreatic cancer SLR — batch 1"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={isCreating}
                        autoComplete="off"
                    />
                    <span className="ev-xsmall-p-dark opacity-75">
                        Leave blank to use the file name
                    </span>
                </div>

                <div className="mb-3 text-start">
                    <label className="ev-small-p-dark ev-bold-reg mb-1">
                        File <span className="text-danger">*</span>
                    </label>

                    <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        style={{ display: "none" }}
                        ref={fileInputRef}
                        onChange={(e) => handleFilePick(e.target.files)}
                    />

                    <div className="d-flex align-items-center gap-2">
                        <button
                            type="button"
                            className="ev-btn-light-fill ev-btn-small flex-shrink-0"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isCreating}
                        >
                            Choose file
                        </button>
                        <div className="ev-card-2 px-3 py-2 ev-radius-xsmall flex-grow-1 text-truncate">
                            <span className={file ? "ev-xsmall-p-dark ev-bold-med" : "ev-xsmall-p-dark opacity-50"}>
                                {file ? file.name : "No file chosen"}
                            </span>
                        </div>
                    </div>

                    {fileError && (
                        <div className="d-flex align-items-center gap-2 mt-2 px-3 py-2 ev-radius-xsmall ev-message-card2">
                            <i className="bi bi-exclamation-triangle flex-shrink-0"></i>
                            <span className="ev-xsmall-p-dark">{fileError}</span>
                        </div>
                    )}
                </div>

                <div className="text-end">
                    <button
                        type="button"
                        className="ev-btn-gradient-fill-round ev-radius-xsmall ev-bold-reg ev-btn-small"
                        onClick={handleCreate}
                        disabled={!file || !!fileError || isCreating}
                    >
                        {isCreating ? (
                            <><i className="bi bi-hourglass-split me-1"></i> Starting...</>
                        ) : (
                            <><i className="bi bi-play-fill"></i> Start Check</>
                        )}
                    </button>
                </div>
            </div>

            {isProcessingPopupVisible &&
                ReactDOM.createPortal(
                    <ProcessingPopup message={processingMessage || "Working..."} />,
                    document.body
                )}

            {popupErrorMessage &&
                ReactDOM.createPortal(
                    <ErrorPopup onClose={() => setPopupErrorMessage("")} message={popupErrorMessage} />,
                    document.body
                )}
        </>
    );
};

export default CreateGenAIRightsCheckModal;